/**
 * useEmployeeFilters Hook
 *
 * Custom hook for managing employee list filters with URL synchronization.
 * The URL is the source of truth for the current view, and filters are also
 * persisted to storage so they are restored when returning to the list from a
 * profile (via breadcrumb, save redirect, or deep link) without a query string.
 *
 * @module domains/personnel/presentation/hooks/useEmployeeFilters
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { getPersonnelStorage } from '@personnel/composition';
import type { EmployeeFiltersStorage } from '@personnel/application/storage/types';

/**
 * Options for setPage. When `replace` is true, the URL change does not create
 * a new history entry — useful for normalizing out-of-range pages without
 * polluting back/forward navigation.
 */
export interface SetPageOptions {
  replace?: boolean;
}

/**
 * Result returned by useEmployeeFilters hook
 */
export interface UseEmployeeFiltersResult {
  /** Current filters state */
  filters: EmployeeFiltersStorage;
  /** Current page (1-indexed) */
  page: number;
  /**
   * Function to update filters.
   *
   * Behavior:
   * - If `newFilters` is identical to the current filters, this is a no-op.
   * - If only `user` (text input) changed, uses `history.replace` (no history spam).
   * - Any other change uses `history.push` and resets page to 1.
   */
  setFilters: (filters: EmployeeFiltersStorage) => void;
  /** Function to update only the current page */
  setPage: (page: number, options?: SetPageOptions) => void;
  /** Function to reset filters to defaults */
  resetFilters: () => void;
  /** Indicates if filters have been initialized from URL */
  isReady: boolean;
}

/** Default (empty) filters. */
export const DEFAULT_FILTERS: EmployeeFiltersStorage = { store: [], role: [], status: [], user: '' };

/**
 * Builds the query string for the employees list from a set of filters.
 * Shared with navigation callers so returning to the list can preserve filters.
 *
 * @param filters - Filters to serialize
 * @param page - Page number (1-indexed); omitted from the query when 1
 * @returns Query string without the leading `?` (empty when no filters/page)
 */
export const buildEmployeeFiltersSearch = (filters: EmployeeFiltersStorage, page = 1): string => {
  const params = new URLSearchParams();

  if (filters.store.length > 0) {
    params.set('store', filters.store.join(','));
  }
  if (filters.role.length > 0) {
    params.set('role', filters.role.join(','));
  }
  if (filters.status.length > 0) {
    params.set('status', filters.status.join(','));
  }
  if (filters.user.trim()) {
    params.set('user', filters.user);
  }
  if (page > 1) {
    params.set('page', String(page));
  }

  return params.toString();
};

/** True when at least one filter is active. */
const hasActiveFilters = (filters: EmployeeFiltersStorage): boolean =>
  filters.store.length > 0 || filters.role.length > 0 || filters.status.length > 0 || filters.user.trim() !== '';

const parseFiltersFromSearch = (search: string): EmployeeFiltersStorage => {
  const params = new URLSearchParams(search);
  return {
    store: params.get('store')?.split(',').filter(Boolean) ?? [],
    role: params.get('role')?.split(',').filter(Boolean) ?? [],
    status: params.get('status')?.split(',').filter(Boolean) ?? [],
    user: params.get('user') ?? '',
  };
};

const areFiltersEqual = (a: EmployeeFiltersStorage, b: EmployeeFiltersStorage): boolean =>
  a.user === b.user &&
  a.store.join(',') === b.store.join(',') &&
  a.role.join(',') === b.role.join(',') &&
  a.status.join(',') === b.status.join(',');

/**
 * Custom hook for managing employee filters with URL synchronization + persistence.
 *
 * Features:
 * - URL is the source of truth for the current view
 * - Filters persisted to storage and restored when landing without a query string
 * - Browser back/forward navigation support
 * - Deep linking support via query parameters
 *
 * @returns {UseEmployeeFiltersResult} Filters state and control functions
 *
 * @example
 * const { filters, setFilters, resetFilters, isReady } = useEmployeeFilters();
 */
export const useEmployeeFilters = (): UseEmployeeFiltersResult => {
  const history = useHistory();
  const location = useLocation();
  const storage = getPersonnelStorage();
  const [isReady, setIsReady] = useState(false);

  const filters: EmployeeFiltersStorage = useMemo(() => parseFiltersFromSearch(location.search), [location.search]);

  const page: number = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const parsed = Number(params.get('page'));
    return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
  }, [location.search]);

  // Restore persisted filters when landing on the list without any URL state
  // (e.g. returning from a profile via breadcrumb/save redirect). Runs once.
  useEffect(() => {
    const params = new URLSearchParams(history.location.search);
    const hasUrlState =
      params.has('store') || params.has('role') || params.has('status') || params.has('user') || params.has('page');

    if (!hasUrlState) {
      const stored = storage.loadEmployeeFilters(DEFAULT_FILTERS);
      if (hasActiveFilters(stored)) {
        const search = buildEmployeeFiltersSearch(stored);
        if (search) {
          history.replace(`${history.location.pathname}?${search}`);
        }
      }
    }

    setIsReady(true);
  }, [history, storage]);

  // Persist the current filters so they survive navigating away and back.
  useEffect(() => {
    if (!isReady) return;
    storage.saveEmployeeFilters({
      store: filters.store,
      role: filters.role,
      status: filters.status,
      user: filters.user,
    });
  }, [filters, isReady, storage]);

  const buildParams = useCallback((nextFilters: EmployeeFiltersStorage, nextPage: number): URLSearchParams => {
    return new URLSearchParams(buildEmployeeFiltersSearch(nextFilters, nextPage));
  }, []);

  const setFilters = useCallback(
    (newFilters: EmployeeFiltersStorage) => {
      const prev = parseFiltersFromSearch(history.location.search);
      // No real change → no-op. Avoids unnecessary history entries and page resets.
      if (areFiltersEqual(prev, newFilters)) return;
      // Only the user text input changed: use replace to avoid history spam from typing.
      // Any other change (store/role/status) is a discrete action and gets a new history entry.
      const onlyUserChanged =
        newFilters.user !== prev.user &&
        newFilters.store.join(',') === prev.store.join(',') &&
        newFilters.role.join(',') === prev.role.join(',') &&
        newFilters.status.join(',') === prev.status.join(',');
      const params = buildParams(newFilters, 1);
      const url = `${history.location.pathname}?${params.toString()}`;
      if (onlyUserChanged) {
        history.replace(url);
      } else {
        history.push(url);
      }
    },
    [buildParams, history],
  );

  const setPage = useCallback(
    (nextPage: number, options?: SetPageOptions) => {
      const currentFilters = parseFiltersFromSearch(history.location.search);
      const params = buildParams(currentFilters, nextPage);
      const url = `${history.location.pathname}?${params.toString()}`;
      if (options?.replace) {
        history.replace(url);
      } else {
        history.push(url);
      }
    },
    [buildParams, history],
  );

  const resetFilters = useCallback(() => {
    storage.removeEmployeeFilters();
    const params = buildParams(DEFAULT_FILTERS, 1);
    const query = params.toString();
    const url = query ? `${history.location.pathname}?${query}` : history.location.pathname;
    history.push(url);
  }, [buildParams, history, storage]);

  return {
    filters,
    page,
    setFilters,
    setPage,
    resetFilters,
    isReady,
  };
};
